import type { ReactNode, SyntheticEvent, CSSProperties, FC } from 'react';
import { createRef, useEffect } from 'react';
import classnames from 'classnames';

import Icon from 'app/components/Icon';
import checkIcon from 'app/icons/check.svg';

import style from './style.scss';

type CheckboxStatus = 'checked' | 'unchecked' | 'indeterminate';

interface Props {
  status: CheckboxStatus;
  label?: ReactNode;
  labelPosition?: 'left' | 'right';
  disabled?: boolean;
  onChange?: (event: SyntheticEvent<HTMLInputElement>) => void;
  className?: string;
  style?: CSSProperties;
  variation?: 'filled' | 'empty' | 'always_checked';
  size?: 'huge' | 'big' | 'medium' | 'small';
  tabIndex?: number;
  labelClassName?: string;
  id?: string;
  transparent?: boolean;
}

const variationClassNameMap = {
  filled: style.container_filled,
  empty: style.container_empty,
  always_checked: style.container_always_checked
};
const sizeClassNameMap = {
  small: style.container_small,
  medium: style.container_medium,
  big: style.container_big,
  huge: style.container_huge
};

const Checkbox: FC<Props> = ({
  status,
  label,
  labelPosition = 'left',
  disabled,
  onChange,
  className,
  style: css,
  variation = 'filled',
  size = 'big',
  tabIndex,
  labelClassName,
  id,
  transparent = false
}) => {
  const checkboxRef = createRef<HTMLInputElement>();

  const setIndeterminate = () => {
    const { current: checkbox } = checkboxRef;
    if (checkbox) {
      if (status === 'indeterminate') {
        checkbox.indeterminate = true;
      } else {
        checkbox.indeterminate = false;
      }
    }
  };

  const blurCheckbox = () => {
    requestAnimationFrame(() => {
      const { current: checkbox } = checkboxRef;
      if (checkbox) {
        checkbox.blur();
      }
    });
  };

  useEffect(setIndeterminate);

  return (
    <label
      className={classnames(
        style.container,
        variationClassNameMap[variation],
        sizeClassNameMap[size],
        className,
        {
          [style.container_disabled]: disabled
        }
      )}
      style={css}
      onMouseUp={blurCheckbox}
    >
      {label && labelPosition === 'left' && (
        <span
          className={classnames(
            style.label,
            style.label_left,
            disabled && style.label_disabled,
            labelClassName
          )}
        >
          {label}
        </span>
      )}
      <input
        ref={checkboxRef}
        type="checkbox"
        checked={status === 'checked'}
        disabled={disabled}
        onChange={onChange}
        className={style.input}
        tabIndex={tabIndex}
        readOnly={!onclick}
        id={id}
      />
      <div className={classnames(style.checkbox, { [style.transparentCheckbox]: transparent })}>
        <Icon href={checkIcon} className={style.checkIcon} />
        <div className={style.indeterminateIndicator} />
      </div>
      {label && labelPosition === 'right' && (
        <span
          className={classnames(
            style.label,
            style.label_right,
            disabled && style.label_disabled,
            labelClassName
          )}
        >
          {label}
        </span>
      )}
    </label>
  );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
export type { CheckboxStatus };
